import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

let nextId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++nextId;
    setToasts(prev => [...prev.slice(-2), { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error:   (msg, duration) => addToast(msg, 'error', duration ?? 6000),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
    info:    (msg, duration) => addToast(msg, 'info', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};

/* ── Toast Container ───────────────────────────────────────── */

const ICONS = {
  success: '✓',
  error:   '✕',
  warning: '⚠',
  info:    'ℹ',
};

const ToastContainer = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div style={styles.container}>
      {toasts.map(t => (
        <div key={t.id} style={{ ...styles.toast, ...styles[t.type] }} role="alert">
          <span style={styles.icon}>{ICONS[t.type]}</span>
          <span style={styles.message}>{t.message}</span>
          <button
            style={styles.close}
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    top: '80px',
    right: '20px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '380px',
    width: 'calc(100vw - 40px)',
  },
  toast: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    fontSize: '14px',
    lineHeight: '1.4',
    animation: 'slideInRight 0.2s ease',
  },
  success: {
    background: '#d4edda',
    color: '#155724',
    borderLeft: '4px solid #28a745',
  },
  error: {
    background: '#f8d7da',
    color: '#721c24',
    borderLeft: '4px solid #dc3545',
  },
  warning: {
    background: '#fff3cd',
    color: '#856404',
    borderLeft: '4px solid #ffc107',
  },
  info: {
    background: '#d1ecf1',
    color: '#0c5460',
    borderLeft: '4px solid #17a2b8',
  },
  icon: {
    fontWeight: 700,
    fontSize: '13px',
    flexShrink: 0,
    marginTop: '1px',
  },
  message: {
    flex: 1,
  },
  close: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    lineHeight: 1,
    opacity: 0.6,
    padding: 0,
    color: 'inherit',
    flexShrink: 0,
  },
};
